import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Filter, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../store/useAppStore";
import { sessionsService } from "../services/sessionsService";
import type { EventSession } from "../types";
import { PageShell } from "../components/PageShell";
import { PageHeader } from "../components/PageHeader";
import { SessionCard } from "../components/SessionCard";
import { EmptyState } from "../components/EmptyState";

type SessionFilter = "all" | "favorites" | "today" | "upcoming";

/**
 * Map raw session.type → UI filter group.
 * workshop + masterclass share one chip ("Мастер-классы") so the list
 * never shows two identical labels for this exhibition.
 */
const TYPE_GROUP_META: Record<string, { group: string; labelKey: string }> = {
  presentation: { group: "presentation", labelKey: "schedule.types.presentation" },
  workshop: { group: "masterclass", labelKey: "schedule.types.masterclass" },
  masterclass: { group: "masterclass", labelKey: "schedule.types.masterclass" },
  panel: { group: "panel", labelKey: "schedule.types.panel" },
  networking: { group: "networking", labelKey: "schedule.types.networking" },
  keynote: { group: "keynote", labelKey: "schedule.types.keynote" },
};

function typeGroup(type: string, t: (key: string) => string): { group: string; label: string } {
  const meta = TYPE_GROUP_META[type];
  return meta ? { group: meta.group, label: t(meta.labelKey) } : { group: type, label: type };
}

export function SchedulePage() {
  const { t, i18n } = useTranslation();
  const { selectedEventId, user } = useAppStore();
  const [filter, setFilter] = useState<SessionFilter>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [allSessions, setAllSessions] = useState<EventSession[]>([]);
  const [favoriteSessions, setFavoriteSessions] = useState<EventSession[]>([]);
  const [loading, setLoading] = useState(true);

  const eventId = selectedEventId ?? "";

  useEffect(() => {
    if (!eventId) {
      setAllSessions([]);
      setFavoriteSessions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [sessions, favorites] = await Promise.all([
          sessionsService.getByEvent(eventId),
          sessionsService.getUserFavorites(user.id, eventId).catch(() => [] as EventSession[]),
        ]);
        if (!cancelled) {
          setAllSessions(sessions);
          setFavoriteSessions(favorites);
          setTypeFilter("all");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eventId, i18n.language, user.id]);

  /** Only types that actually exist on this exhibition's programme. */
  const typeOptions = useMemo(() => {
    const byGroup = new Map<string, string>();
    for (const session of allSessions) {
      const { group, label } = typeGroup(session.type, t);
      if (!byGroup.has(group)) byGroup.set(group, label);
    }
    return Array.from(byGroup.entries()).map(([value, label]) => ({ value, label }));
  }, [allSessions, t]);

  const filteredSessions = useMemo(() => {
    let sessions = allSessions;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart);
    todayEnd.setDate(todayEnd.getDate() + 1);

    switch (filter) {
      case "favorites":
        sessions = favoriteSessions;
        break;
      case "today":
        sessions = sessions.filter((s) => {
          const start = new Date(s.startTime);
          return start >= todayStart && start < todayEnd;
        });
        break;
      case "upcoming":
        sessions = sessions.filter((s) => new Date(s.startTime) > now);
        break;
    }

    if (typeFilter !== "all") {
      sessions = sessions.filter((s) => typeGroup(s.type, t).group === typeFilter);
    }

    return sessions.sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    );
  }, [allSessions, favoriteSessions, filter, t, typeFilter]);

  const sessionsByDate = useMemo(() => {
    const grouped: Record<string, EventSession[]> = {};
    filteredSessions.forEach((session) => {
      const tz = session.venueTimeZone ?? "Europe/Moscow";
      const date = new Date(session.startTime).toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: tz,
      });
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(session);
    });
    return grouped;
  }, [filteredSessions, i18n.language]);

  return (
    <>
      <PageHeader
        title={t("schedule.title")}
        subtitle={t("schedule.subtitle", { n: filteredSessions.length })}
        back
      />
      <PageShell>
        <div className="mt-3 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1"
              style={{ scrollbarWidth: "none" }}
            >
              {[
                { value: "all" as SessionFilter, label: t("schedule.filter_all") },
                { value: "favorites" as SessionFilter, label: t("schedule.filter_favorites") },
                { value: "today" as SessionFilter, label: t("schedule.filter_today") },
                { value: "upcoming" as SessionFilter, label: t("schedule.filter_upcoming") },
              ].map((option) => {
                const isActive = filter === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFilter(option.value)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium whitespace-nowrap transition-all ${
                      isActive
                        ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/20"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </motion.div>

          {typeOptions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.3 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Filter size={14} className="text-gray-400" />
                <span className="text-[12px] text-gray-500">{t("schedule.type_label")}</span>
              </div>
              <div
                className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1"
                style={{ scrollbarWidth: "none" }}
              >
                <button
                  type="button"
                  onClick={() => setTypeFilter("all")}
                  className={`px-3 py-1.5 rounded-lg text-[12px] font-medium whitespace-nowrap transition-all ${
                    typeFilter === "all"
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-white text-gray-600 border border-gray-200"
                  }`}
                >
                  {t("schedule.type_all")}
                </button>
                {typeOptions.map((option) => {
                  const isActive = typeFilter === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setTypeFilter(option.value)}
                      className={`px-3 py-1.5 rounded-lg text-[12px] font-medium whitespace-nowrap transition-all ${
                        isActive
                          ? "bg-indigo-100 text-indigo-700"
                          : "bg-white text-gray-600 border border-gray-200"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {loading ? (
            <div className="flex justify-center py-16 text-gray-400">
              <Loader2 size={28} className="animate-spin" />
            </div>
          ) : Object.keys(sessionsByDate).length > 0 ? (
            Object.entries(sessionsByDate).map(([date, sessions], dateIndex) => (
              <motion.div
                key={date}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: dateIndex * 0.05, duration: 0.3 }}
              >
                <div className="flex items-center gap-2 mb-3 px-1">
                  <Calendar size={16} className="text-indigo-500" />
                  <h3 className="text-[15px] text-gray-900">{date}</h3>
                  <span className="text-[12px] text-gray-400">({sessions.length})</span>
                </div>
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <SessionCard key={session.id} session={session} />
                  ))}
                </div>
              </motion.div>
            ))
          ) : (
            <EmptyState
              icon={<Calendar size={32} />}
              title={t("schedule.empty_title")}
              description={t("schedule.empty_desc")}
            />
          )}
        </div>
      </PageShell>
    </>
  );
}
